import { Alert, Button, Typography } from "@mui/material"
import { useState } from "react"
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query"
import type { TemplateWriteAccess } from "../hooks/useVersionDraft"
import type { TemplateVersion } from "../interface/publications"
import { useGetTemplateVersionQuery } from "../services/publications/templatesApi"
import { PostBotNavigation } from "../components/common/postBotNavigation"
import { PostBotPageLayout } from "../components/common/postBotPageLayout"
import { TemplateVersionEditor } from "../components/templates/templateVersionEditor"
import { TemplateQueryFeedback } from "../components/templates/templateQueryFeedback"
import {
  isTemplateId,
  readTemplateReturnContext,
  templatePath,
  templatesPath,
} from "../components/templates/templateNavigation"

/** Blank creation never inherits content. A clone is addressable and parent-checked. */
export default function TemplateAuthorPage(access: TemplateWriteAccess) {
  const { templateId } = useParams()
  const { state } = useLocation()
  const [search] = useSearchParams()
  const sourceId = search.get("sourceVersionId")
  return (
    <TemplateAuthorSession
      key={`${templateId ?? "new"}:${sourceId ?? "blank"}`}
      {...access}
      templateId={templateId}
      sourceId={sourceId}
      returnState={state}
    />
  )
}

function TemplateAuthorSession({
  templateId,
  sourceId,
  returnState,
  ...access
}: TemplateWriteAccess & {
  templateId?: string
  sourceId: string | null
  returnState: unknown
}) {
  const valid =
    (!templateId || isTemplateId(templateId)) &&
    (sourceId === null || (!!templateId && isTemplateId(sourceId)))
  const query = useGetTemplateVersionQuery(
    valid && sourceId ? sourceId : skipToken,
  )
  const detail =
    !query.error &&
    query.currentData?.id === sourceId &&
    query.currentData.templateId === templateId
      ? query.currentData
      : undefined
  // A clone is a snapshot, not a subscription that can reset a dirty editor.
  const [snapshot, setSnapshot] = useState<TemplateVersion>()
  if (!snapshot && detail) setSnapshot(detail)
  const source = snapshot ?? detail
  const context = readTemplateReturnContext(returnState)
  return (
    <PostBotPageLayout>
      <PostBotNavigation />
      <Button
        component={Link}
        to={templateId ? templatePath(templateId) : templatesPath}
        state={context}
        sx={{ alignSelf: "flex-start" }}
      >
        Назад к шаблонам
      </Button>
      <Typography variant="h5">
        {templateId ? "Редактирование шаблона" : "Новый шаблон"}
      </Typography>
      {!valid || (sourceId && !query.isFetching && !query.error && !source) ? (
        <Alert severity="error">Версия недоступна для этого шаблона</Alert>
      ) : sourceId && !source ? (
        <TemplateQueryFeedback query={query} />
      ) : (
        <TemplateVersionEditor
          {...access}
          draftKey={
            source
              ? `from:${templateId}:${source.id}`
              : templateId
                ? `blank:${templateId}`
                : "new-template"
          }
          templateId={templateId}
          sourceVersion={source}
          returnContext={context}
        />
      )}
    </PostBotPageLayout>
  )
}
